import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const SAVED_NEWS_TTL_MS = 2 * 24 * 60 * 60 * 1000;

const STATE_REGION_MAP = {
  alabama: 'southeast',
  alaska: 'west',
  arizona: 'west',
  arkansas: 'south',
  california: 'west',
  colorado: 'west',
  connecticut: 'northeast',
  delaware: 'south',
  florida: 'southeast',
  georgia: 'southeast',
  hawaii: 'west',
  idaho: 'west',
  illinois: 'midwest',
  indiana: 'midwest',
  iowa: 'midwest',
  kansas: 'midwest',
  kentucky: 'south',
  louisiana: 'south',
  maine: 'northeast',
  maryland: 'south',
  massachusetts: 'northeast',
  michigan: 'midwest',
  minnesota: 'midwest',
  mississippi: 'south',
  missouri: 'midwest',
  montana: 'west',
  nebraska: 'midwest',
  nevada: 'west',
  'new hampshire': 'northeast',
  'new jersey': 'northeast',
  'new mexico': 'west',
  'new york': 'northeast',
  'north carolina': 'southeast',
  'north dakota': 'midwest',
  ohio: 'midwest',
  oklahoma: 'south',
  oregon: 'west',
  pennsylvania: 'northeast',
  'rhode island': 'northeast',
  'south carolina': 'southeast',
  'south dakota': 'midwest',
  tennessee: 'southeast',
  texas: 'south',
  utah: 'west',
  vermont: 'northeast',
  virginia: 'south',
  washington: 'west',
  'west virginia': 'south',
  wisconsin: 'midwest',
  wyoming: 'west',
  'district of columbia': 'south',
};

const sanitize = (value) => String(value || '').trim();

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getFeedRunMs = (article) =>
  Number(article?.feedRunAtMs || toMillis(article?.fetchedAt) || 0);

const getLatestFeedRunMs = (articles = []) =>
  articles.reduce((latest, article) => Math.max(latest, getFeedRunMs(article)), 0);

const getCommentActivityMs = (article) => toMillis(article?.lastCommentAt);

const getRecentCommentSortDelta = (a, b, latestFeedRunMs) => {
  const aRecent = getCommentActivityMs(a) > latestFeedRunMs;
  const bRecent = getCommentActivityMs(b) > latestFeedRunMs;
  if (aRecent !== bRecent) return bRecent ? 1 : -1;
  if (!aRecent || !bRecent) return 0;

  const commentDelta = Number(b.commentCount || 0) - Number(a.commentCount || 0);
  if (commentDelta !== 0) return commentDelta;
  return getCommentActivityMs(b) - getCommentActivityMs(a);
};

export const getCurrentNewsCycleKey = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const hour = Number(parts.hour);

  const base = new Date(Date.UTC(year, month - 1, day));
  if (hour < 8) {
    base.setUTCDate(base.getUTCDate() - 1);
  }

  return `${String(base.getUTCFullYear()).padStart(4, '0')}-${String(
    base.getUTCMonth() + 1
  ).padStart(2, '0')}-${String(base.getUTCDate()).padStart(2, '0')}`;
};

export const getTimeAgo = (value) => {
  const date =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
      ? value
      : value
      ? new Date(value)
      : null;

  if (!date || Number.isNaN(date.getTime())) return '';

  const deltaMs = Date.now() - date.getTime();
  const minutes = Math.max(1, Math.floor(deltaMs / (60 * 1000)));
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export const getLocalArticleScore = (article, profile) => {
  const baseScore = Number(article?.score || 0);
  const stateLower = sanitize(profile?.state).toLowerCase();
  const cityLower = sanitize(profile?.city).toLowerCase();
  const matchedStatesLower = Array.isArray(article?.matchedStatesLower)
    ? article.matchedStatesLower
    : [];
  const matchedCitiesLower = Array.isArray(article?.matchedCitiesLower)
    ? article.matchedCitiesLower
    : [];
  const regionalKeywords = Array.isArray(article?.regionalKeywords)
    ? article.regionalKeywords
    : [];

  let localScore = baseScore;
  if (stateLower && matchedStatesLower.includes(stateLower)) {
    localScore += 5;
  }
  if (cityLower && matchedCitiesLower.includes(cityLower)) {
    localScore += 3;
  }
  const region = STATE_REGION_MAP[stateLower];
  if (region && regionalKeywords.includes(region)) {
    localScore += 2;
  }

  return localScore;
};

export const sortNationalArticles = (articles = []) => {
  const latestFeedRunMs = getLatestFeedRunMs(articles);
  return [...articles].sort((a, b) => {
    const pinDelta = Number(b.adminPinPriority || 0) - Number(a.adminPinPriority || 0);
    if (pinDelta !== 0) return pinDelta;

    const aPinnedAt = toMillis(a.adminPinnedAt);
    const bPinnedAt = toMillis(b.adminPinnedAt);
    if (bPinnedAt !== aPinnedAt) return bPinnedAt - aPinnedAt;

    const recentCommentDelta = getRecentCommentSortDelta(a, b, latestFeedRunMs);
    if (recentCommentDelta !== 0) return recentCommentDelta;

    const feedRunDelta = getFeedRunMs(b) - getFeedRunMs(a);
    if (feedRunDelta !== 0) return feedRunDelta;

    const commentDelta = Number(b.commentCount || 0) - Number(a.commentCount || 0);
    if (commentDelta !== 0) return commentDelta;

    const publishedDelta = Number(b.publishedAtMs || 0) - Number(a.publishedAtMs || 0);
    if (publishedDelta !== 0) return publishedDelta;

    return Number(b.score || 0) - Number(a.score || 0);
  });
};

export const sortLocalArticles = (articles = [], profile) => {
  const latestFeedRunMs = getLatestFeedRunMs(articles);
  return [...articles].sort((a, b) => {
    const pinDelta = Number(b.adminPinPriority || 0) - Number(a.adminPinPriority || 0);
    if (pinDelta !== 0) return pinDelta;

    const recentCommentDelta = getRecentCommentSortDelta(a, b, latestFeedRunMs);
    if (recentCommentDelta !== 0) return recentCommentDelta;

    const feedRunDelta = getFeedRunMs(b) - getFeedRunMs(a);
    if (feedRunDelta !== 0) return feedRunDelta;

    const commentDelta = Number(b.commentCount || 0) - Number(a.commentCount || 0);
    if (commentDelta !== 0) return commentDelta;

    const localDelta = getLocalArticleScore(b, profile) - getLocalArticleScore(a, profile);
    if (localDelta !== 0) return localDelta;

    return Number(b.publishedAtMs || 0) - Number(a.publishedAtMs || 0);
  });
};

export const saveNewsArticle = async (article) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error('Authentication required.');

  const savedRef = doc(db, 'users', user.uid, 'savedNews', article.id);
  await setDoc(savedRef, {
    originalArticleId: article.id,
    title: article.title || '',
    summary: article.summary || '',
    whatItMeans: article.whatItMeans || '',
    source: article.source || '',
    url: article.url || '',
    imageUrl: article.imageUrl || '',
    publishedAt: article.publishedAt || null,
    publishedAtMs: Number(article.publishedAtMs || 0),
    tags: Array.isArray(article.tags) ? article.tags : [],
    score: Number(article.score || 0),
    articleCollection: article.articleCollection || 'newsArticles',
    savedAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + SAVED_NEWS_TTL_MS),
  }, { merge: true });
};

export const unsaveNewsArticle = async (articleId) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error('Authentication required.');
  await deleteDoc(doc(db, 'users', user.uid, 'savedNews', articleId));
};

export const pinNewsArticle = async (articleId) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error('Authentication required.');
  await setDoc(doc(db, 'users', user.uid, 'pinnedNews', articleId), {
    articleId,
    pinnedAt: serverTimestamp(),
  }, { merge: true });
};

export const unpinNewsArticle = async (articleId) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error('Authentication required.');
  await deleteDoc(doc(db, 'users', user.uid, 'pinnedNews', articleId));
};

export const setAdminPinnedArticle = async (
  articleId,
  shouldPin,
  priority = 1,
  articleCollection = 'newsArticles'
) => {
  await updateDoc(doc(db, articleCollection, articleId), {
    adminPinned: shouldPin,
    adminPinnedAt: shouldPin ? serverTimestamp() : null,
    adminPinPriority: shouldPin ? Number(priority || 1) : 0,
  });
};

export const setNewsArticleHiddenState = async (
  articleId,
  shouldHide,
  articleCollection = 'newsArticles'
) => {
  await updateDoc(doc(db, articleCollection, articleId), {
    isHidden: shouldHide,
    hiddenBy: shouldHide ? 'admin' : '',
    hiddenReason: shouldHide ? 'Hidden by admin' : '',
    hiddenAt: shouldHide ? serverTimestamp() : null,
    flaggedForReview: shouldHide ? true : false,
  });
};

export const addNewsComment = async (
  articleId,
  profile,
  text,
  articleCollection = 'newsArticles'
) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error('Authentication required.');

  const cleanText = sanitize(text);
  if (!cleanText) throw new Error('Comment cannot be empty.');

  const commentRef = doc(collection(db, articleCollection, articleId, 'comments'));
  await setDoc(commentRef, {
    userId: user.uid,
    userName: profile?.firstInitialLastName || 'Unknown',
    rank: profile?.rank || '',
    agency: profile?.agency || '',
    text: cleanText,
    createdAt: serverTimestamp(),
  });
};

export const deleteNewsComment = async (
  articleId,
  commentId,
  articleCollection = 'newsArticles'
) => {
  const commentRef = doc(db, articleCollection, articleId, 'comments', commentId);
  const commentSnap = await getDoc(commentRef);
  if (!commentSnap.exists()) return;

  await deleteDoc(commentRef);
};
